import { useEffect, useMemo, useRef } from 'react';
import { clsx } from 'clsx';
import { isEmpty } from 'lodash';
import { Helmet } from 'react-helmet';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory, useLocation } from 'react-router-dom';

import {
  Box,
  Button,
  CircularProgress,
  Container,
  Divider,
  Grid,
  Typography,
} from '@material-ui/core';
import { useGetProfile } from 'hooks/user.hook';

import { useGetLinkRequestsService } from 'features/user/services/get-link-requests.service';

import { NutritionistFinder } from 'features/nutritionists/components/nutritionist-finder/nutritionist-finder.component';
import { NutritionistCard } from 'features/nutritionists/components/nutritionist-card/nutritionist-card.component';
import { NutritionistSearchText } from 'features/nutritionists/components/nutritionist-search-text/nutritionist-search-text.component';
import { nutritionistsSelector } from 'features/nutritionists/selectors/nutritionists.selector';
import { useGetNutritionistsService } from 'features/nutritionists/services/get-nutritionists.service';
import { nutritionistTestIsSearchingByTextSelector } from '../../selectors/nutritionist-test.selector';
import { setIsSearchingByText } from '../../state/nutritionist-test.slice';

import { useStyles } from './nutritionists-page.styles';

export const NutritionistsPage = () => {
  const { search } = useLocation();

  const history = useHistory();

  const isSearchingByText = useSelector(
    nutritionistTestIsSearchingByTextSelector,
  );

  const urlParams = useMemo(() => {
    const searchParams = new URLSearchParams(search);

    console.log('Search params: ', searchParams);

    return {
      page: searchParams.get('page') || null,
      limit: searchParams.get('limit') || null,
      mainFilter: searchParams.get('mainFilter') || null,
      modes: searchParams.get('modes') || null,
      keySpecialities: searchParams.get('keySpecialities') || null,
    };
  }, [search]);

  const { isAuthenticated } = useSelector(state => state.authentication);

  const observerTarget = useRef(null);

  const getUserDataService = useGetProfile(isAuthenticated);
  const getLinkRequestsService = useGetLinkRequestsService(isAuthenticated);
  const getNutritionistsService = useGetNutritionistsService();

  const nutritionists = useSelector(nutritionistsSelector);

  const dispatch = useDispatch();

  const classes = useStyles();

  const onNextPageHandler = () => {
    const searchParams = new URLSearchParams(search);
    const nextPage = Number(searchParams.get('page')) + 1;
    searchParams.set('page', nextPage.toString());
    history.replace({
      pathname: history.location.pathname,
      search: searchParams.toString(),
    });
  };

  const onPreviousPageHandler = () => {
    const searchParams = new URLSearchParams(search);
    const previousPage = Number(searchParams.get('page')) - 1;
    searchParams.set('page', previousPage.toString());
    history.replace({
      pathname: history.location.pathname,
      search: searchParams.toString(),
    });
  };

  const onResetHandler = () => {
    dispatch(setIsSearchingByText(false));
    const page = 0;
    const queryParams = `?page=${page + 1}&limit=10`;

    history.replace({
      pathname: history.location.pathname,
      search: queryParams,
    });

    getNutritionistsService.mutate({ queryParams: `?page=${page}&limit=10` });
  };

  useEffect(() => {
    const { page, limit, modes, keySpecialities } = urlParams;

    let queryParams = '?';

    if (page && limit) {
      queryParams = queryParams + `page=${Number(page) - 1}&limit=${limit}`;
    }

    if (modes) {
      queryParams = queryParams + `&modes=${modes}`;
    }

    if (keySpecialities) {
      queryParams = queryParams + `&keySpecialities=${keySpecialities}`;
    }

    getNutritionistsService.mutate({ queryParams });
  }, [urlParams.page, urlParams.limit, urlParams.modes, urlParams.keySpecialities]);

  useEffect(() => {
    if (observerTarget.current) {
      observerTarget.current?.scrollIntoView({ behavior: 'smooth' });
    }
  }, [urlParams.page]);

  if (
    getNutritionistsService.isError ||
    getUserDataService.isError ||
    getLinkRequestsService.isError
  ) {
    return (
      <Box pt={4}>
        <Typography>
          No se pudo cargar la información. Intenta más tarde
        </Typography>
      </Box>
    );
  }

  if (
    getNutritionistsService.isLoading ||
    getUserDataService.isLoading ||
    getLinkRequestsService.isLoading
  ) {
    return (
      <Box pt={4} display="flex" justifyContent="center">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      <Helmet>
        <title>Nutriólogos | Wellnub</title>
      </Helmet>
      <Container className={classes.container}>
        <Box>
          <Typography
            variant="h2"
            component="h1"
            className={clsx(classes.center, classes.uppercase)}
          >
            Conecta con tu nutriólogo ideal
          </Typography>
          <Typography
            variant="h5"
            className={clsx(classes.center, classes.subtitle)}
          >
            Descubre los especialistas en Wellnub
          </Typography>
        </Box>

        <NutritionistSearchText />

        <Divider />

        {!isSearchingByText && <NutritionistFinder />}

        {isSearchingByText && (
          <Box className={classes.group}>
            <Typography variant="h6" className={classes.sectionTitle}>
              Resultados encontrados: {nutritionists.length}
            </Typography>
            <Button variant="contained" onClick={onResetHandler}>
              Limpiar filtros/busqueda
            </Button>
          </Box>
        )}

        <div ref={observerTarget} />

        {getNutritionistsService.isLoading && (
          <Box display="flex" alignItems="center" justifyContent="center">
            <CircularProgress />
          </Box>
        )}

        {isEmpty(nutritionists) && (
          <Typography align="center" variant="h6" className={classes.center}>
            No tenemos coincidencias para lo que estás buscando. Por favor,
            vuelve a intentarlo
          </Typography>
        )}

        {!isEmpty(nutritionists) && (
          <>
            <Grid container spacing={4}>
              {nutritionists.map(nutritionist => (
                <Grid item xs={12} sm={6} key={nutritionist._id}>
                  <NutritionistCard nutritionist={nutritionist} />
                </Grid>
              ))}
            </Grid>
            {!isSearchingByText && (
              <Box
                pt={2}
                display="flex"
                alignItems="center"
                justifyContent="space-evenly"
              >
                <Button
                  variant="contained"
                  color="primary"
                  disabled={
                    getNutritionistsService.isLoading || urlParams.page === '1'
                  }
                  className={classes.buttonDisabled}
                  onClick={onPreviousPageHandler}
                >
                  Anterior
                </Button>
                <Button
                  variant="contained"
                  color="primary"
                  disabled={getNutritionistsService.isLoading}
                  onClick={onNextPageHandler}
                >
                  Siguiente
                </Button>
              </Box>
            )}
          </>
        )}
      </Container>
    </Box>
  );
};
